import { type FC, useEffect } from "react";
import AssessmentPerformanceBody from "features/Library/Assessments/Performance/AssessmentPerformanceBody";
import { type RegisterBreadcrumbCallback } from "features/Library/Common/Hooks/usePerformanceBreadcrumbs";
import { type PerformanceFilter } from "features/Library/Common/utils/performanceUtils";
import EmailPerformanceBody from "features/Library/Communications/Emails/EmailDetails/Performance/EmailPerformanceBody";
import FlowPerformanceBody from "features/Library/Flows/Performance/FlowPerformanceBody";
import PdfPerformanceBody from "features/Library/Pdfs/Performance/PdfPerformanceBody";
import SurveyPerformanceBody from "features/Library/Surveys/Performance/SurveyPerformanceBody";
import VideoPerformanceBody from "features/Library/Videos/Performance/VideoPerformanceBody";
import { EntityType } from "features/Library/Flows/Designer/types";
import EmbeddedVideoPerformanceBody from "features/Library/EmbeddedVideos/Performance/EmbeddedVideoPerformanceBody";
import ThreatDefenseBody from "features/Library/SimulatedPhishing/Campaigns/Details/Reporting/ThreatDefenseBody";
import LinksReportBody from "../../../../Library/ExternalLinks/Performance/LinksReportBody";
import MessagePerformanceBody from "features/Library/Communications/Messages/Performance/MessagePerformanceBody";

export interface Props {
  id: number;
  type: EntityType;
  name: string;
  filter: PerformanceFilter;
  registerBreadcrumb: RegisterBreadcrumbCallback;
  onReset: () => void;
  goBackNLevels: (n: number) => void;
  includeAccounts: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
}

export const ContentReportDrilldown: FC<Props> = ({
  id,
  type,
  name,
  filter,
  registerBreadcrumb,
  onReset,
  goBackNLevels,
  includeAccounts,
  childAccountId,
  hasCreateAccountsAddOn,
}) => {
  useEffect(() => {
    registerBreadcrumb({ text: name, action: onReset });
  }, [name, onReset, registerBreadcrumb]);

  switch (type) {
    case EntityType.Flow:
      return (
        <FlowPerformanceBody
          flowId={id}
          flowTitle={name}
          dateFilter={filter}
          registerBreadcrumb={registerBreadcrumb}
          hasCreateAccountsAddOn={hasCreateAccountsAddOn}
          childAccountId={childAccountId}
        />
      );
    case EntityType.Video:
      return (
        <VideoPerformanceBody
          includeAccounts={includeAccounts}
          videoId={id}
          videoTitle={name}
          videoFilter={filter}
          childAccountId={childAccountId}
          hasCreateAccountsAddOn={hasCreateAccountsAddOn}
        />
      );
    case EntityType.EmbeddedVideo:
      return (
        <EmbeddedVideoPerformanceBody
          embeddedVideoTitle={name}
          includeAccounts={includeAccounts}
          embeddedVideoId={id}
          filter={filter}
          childAccountId={childAccountId}
          hasCreateAccountsAddOn={hasCreateAccountsAddOn}
        />
      );
    case EntityType.Assessment:
      return (
        <AssessmentPerformanceBody
          entityId={id}
          entityTitle={name}
          dateFilter={filter}
          registerBreadcrumb={registerBreadcrumb}
          hideDrilldownHeader
          includeAccounts={includeAccounts}
          childAccountId={childAccountId}
          hasCreateAccountsAddOn={hasCreateAccountsAddOn}
        />
      );
    case EntityType.Survey:
      return (
        <SurveyPerformanceBody
          surveyId={id}
          surveyTitle={name}
          dateFilter={filter}
          registerBreadcrumb={registerBreadcrumb}
          hideDrilldownHeader
          includeAccounts={includeAccounts}
          childAccountId={childAccountId}
          hasCreateAccountsAddOn={hasCreateAccountsAddOn}
        />
      );
    case EntityType.Email:
      return (
        <EmailPerformanceBody
          emailId={id}
          emailTitle={name}
          // Ignoring until "isDistinct" is added to PerformanceFilter
          // @ts-ignore
          dateFilter={filter}
          registerBreadcrumb={registerBreadcrumb}
          includeAccounts={includeAccounts}
          childAccountId={childAccountId}
          hasCreateAccountsAddOn={hasCreateAccountsAddOn}
        />
      );
    case EntityType.Pdf:
      return (
        <PdfPerformanceBody
          pdfId={id}
          pdfTitle={name}
          pdfFilter={filter}
          includeAccounts={includeAccounts}
          childAccountId={childAccountId}
          hasCreateAccountsAddOn={hasCreateAccountsAddOn}
        />
      );
    case EntityType.Campaign:
      return (
        <ThreatDefenseBody
          registerBreadcrumb={registerBreadcrumb}
          goBackNLevels={goBackNLevels}
          campaignId={id}
          campaignName={name}
          filter={filter}
          includeAccounts={includeAccounts}
          childAccountId={childAccountId}
          hasCreateAccountsAddOn={hasCreateAccountsAddOn}
        />
      );
    case EntityType.Link:
      return (
        <LinksReportBody
          filter={filter}
          linkName={name}
          linkId={id}
          includeAccounts={includeAccounts}
          childAccountId={childAccountId}
          hasCreateAccountsAddOn={hasCreateAccountsAddOn}
        />
      );
    case EntityType.Message:
      return (
        <MessagePerformanceBody
          filter={filter}
          messageId={id}
          messageName={name}
          includeAccounts={includeAccounts}
          registerBreadcrumb={registerBreadcrumb}
        />
      );
    default:
      return <></>;
  }
};
